import { server$ } from "@builder.io/qwik-city";
import { getClerk } from "./clerk-server";
import { type ClerkAPIResponseError } from "@clerk/clerk-js";
import { UserSelect } from "./schemas";

export interface AuthRequestStateNotSignedIn {
  isSignedIn: false;
}

export interface AuthRequestStateSignedIn {
  isSignedIn: true;
  user: UserSelect;
}

export type AuthRequestState =
  | AuthRequestStateNotSignedIn
  | AuthRequestStateSignedIn;

export function setAuthRequestState(
  sharedMap: Map<string, unknown>,
  state: AuthRequestState,
): void {
  sharedMap.set("auth", state);
}

export function getAuthRequestState(
  sharedMap: Map<string, unknown>,
): AuthRequestState {
  return (sharedMap.get("auth") ?? { isSignedIn: false }) as AuthRequestState;
}

export function isClerkAPIResponseError(
  err: any,
): err is ClerkAPIResponseError {
  return "clerkError" in err;
}

export const signOut = server$(async function () {
  const cookieOptions = {
    path: "/",
    secure: true,
    httpOnly: true,
    sameSite: "lax" as const,
    expires: new Date(0),
  };

  // First try setting to empty value
  this.cookie.set("userId", "", cookieOptions);
  this.cookie.set("coachName", "", cookieOptions);

  // Then delete with same options
  this.cookie.delete("userId", cookieOptions);
  this.cookie.delete("coachName", cookieOptions);

  // Redirect to ensure clean state
});

export type UpdateUserParams = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

export const updateClerkUser = server$(async function (
  userId: string,
  params: UpdateUserParams,
) {
  const clerkClient = getClerk();
  if (!clerkClient) {
    throw new Error("Clerk client not initialized");
  }
  await clerkClient.users.updateUser(userId, params);
});

export const updateClerkEmailAddresses = server$(async function (
  emailAddressId: string,
) {
  const clerkClient = getClerk();
  if (!clerkClient) {
    throw new Error("Clerk client not initialized");
  }
  const response = await clerkClient.emailAddresses.updateEmailAddress(
    emailAddressId,
    {
      primary: true,
      verified: true,
    },
  );

  return response.id;
});
